import { useEffect, useState } from 'react';
import api from '@/utils/api';
import { toast } from 'sonner';
import { Radar, RefreshCw, CheckCircle2, XCircle, Search, Mail } from 'lucide-react';

const fmtDt = (s) => (s ? new Date(s).toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' }) : '—');
const REL_STYLE = {
  hoch: 'bg-red-100 text-red-700',
  mittel: 'bg-amber-100 text-amber-700',
  niedrig: 'bg-slate-100 text-slate-600',
};

// Aug 2026 — Regel-Radar: monatlicher KI-Scan amtlicher Quellen + Satz-Wächter (Jahreswechsel)
export default function RegulatoryRadarPanel() {
  const [data, setData] = useState(null);
  const [yearEnd, setYearEnd] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [openScan, setOpenScan] = useState(null);

  const load = () => {
    api.get('/admin/regulatory-scans').then((r) => {
      setData(r.data);
      setOpenScan((r.data.scans || [])[0]?.id || null);
    }).catch(() => toast.error('Regel-Radar konnte nicht geladen werden'));
    api.get('/admin/year-end-status').then((r) => setYearEnd(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const runScan = async () => {
    setScanning(true);
    toast.info('Scan läuft — Quellen werden abgerufen und per KI analysiert (bis zu 90 s) …');
    try {
      const r = await api.post('/admin/regulatory-scans/run');
      if (r.data.status === 'ok') {
        toast.success(`Scan fertig: ${r.data.findings.length} relevante Meldung(en). ` +
          (r.data.email_sent ? 'Mail versendet.' : 'Mail-Versand fehlgeschlagen (lokal erwartet).'));
      } else {
        toast.error(`Scan fehlgeschlagen: ${r.data.error}`);
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Scan fehlgeschlagen');
    }
    setScanning(false);
  };

  if (!data) return <p className="text-gray-500 py-8">Lade Regel-Radar …</p>;

  return (
    <div className="space-y-6" data-testid="admin-radar-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Radar className="h-5 w-5 text-emerald-600" /> Regel-Radar
          </h2>
          <p className="text-sm text-gray-500">
            Monatlicher KI-Scan amtlicher Quellen (BMF AT/DE, WKO, ESTV) — Bericht per Mail an{' '}
            <strong>{data.recipient}</strong>. Modell: claude-fable-5, Kosten im Tab „KI-Kosten“ (User „system-radar“).
          </p>
        </div>
        <button onClick={runScan} disabled={scanning} data-testid="radar-run-scan-btn"
                className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50">
          <RefreshCw className={`h-4 w-4 ${scanning ? 'animate-spin' : ''}`} />
          {scanning ? 'Scannt …' : 'Jetzt scannen'}
        </button>
      </div>

      {/* Satz-Wächter */}
      {yearEnd && (
        <div className="rounded-xl border bg-white p-4" data-testid="radar-yearend-card">
          <h3 className="text-sm font-semibold mb-1">
            Satz-Wächter: Jahreswechsel {yearEnd.next_year}
            {yearEnd.all_done
              ? <span className="ml-2 text-emerald-600 text-xs">alle Auto-Checks grün</span>
              : <span className="ml-2 text-red-600 text-xs">offene Punkte</span>}
          </h3>
          <p className="text-xs text-gray-500 mb-3">
            Reminder-Mails: 1. Nov, 1. Dez, dann wöchentlich bis alles gepflegt ist
            ({(yearEnd.reminders_sent || []).length} bisher versendet).
          </p>
          <ul className="space-y-1.5">
            {yearEnd.checklist.map((i) => (
              <li key={i.key} className="flex items-start gap-2 text-sm" data-testid={`yearend-item-${i.key}`}>
                {i.ok === true && <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />}
                {i.ok === false && <XCircle className="h-4 w-4 text-red-600 mt-0.5 shrink-0" />}
                {i.ok === null && <Search className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" />}
                <span>
                  {i.label}
                  {i.hint && <span className="block text-xs text-gray-400">{i.hint}</span>}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Scan-Historie */}
      <div className="rounded-xl border bg-white" data-testid="radar-scan-history">
        <h3 className="text-sm font-semibold px-4 pt-4">Scan-Historie</h3>
        {(data.scans || []).length === 0 ? (
          <p className="p-4 text-sm text-gray-500" data-testid="radar-empty-state">
            Noch kein Scan gelaufen. In Produktion läuft der Scan automatisch am Monatsanfang —
            oder starte ihn jetzt manuell.
          </p>
        ) : (
          <div className="divide-y mt-2">
            {data.scans.map((s) => (
              <div key={s.id} className="px-4 py-3">
                <button className="flex w-full items-center justify-between text-left text-sm"
                        data-testid={`radar-scan-row-${s.id}`}
                        onClick={() => setOpenScan(openScan === s.id ? null : s.id)}>
                  <span>
                    <strong>{fmtDt(s.run_at)}</strong>
                    <span className="ml-2 text-xs text-gray-500">
                      {s.trigger === 'auto' ? 'automatisch' : 'manuell'} · {s.model || '—'} ·{' '}
                      {(s.cost_eur || 0).toFixed(4)} €
                    </span>
                  </span>
                  <span className="flex items-center gap-2 text-xs">
                    {s.status === 'ok'
                      ? <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-emerald-700">{(s.findings || []).length} Meldung(en)</span>
                      : <span className="rounded-full bg-red-100 px-2 py-0.5 text-red-700">Fehler</span>}
                    <Mail className={`h-3.5 w-3.5 ${s.email_sent ? 'text-emerald-600' : 'text-gray-300'}`} />
                  </span>
                </button>
                {openScan === s.id && (
                  <div className="mt-3 space-y-2" data-testid={`radar-scan-details-${s.id}`}>
                    {s.error && <p className="text-xs text-red-600">{s.error}</p>}
                    {(s.findings || []).map((f, i) => (
                      <div key={i} className="rounded-lg border bg-slate-50 p-3 text-sm" data-testid={`radar-finding-${s.id}-${i}`}>
                        <div className="flex flex-wrap items-center gap-2">
                          <strong>[{f.country}] {f.title}</strong>
                          <span className={`rounded-full px-2 py-0.5 text-[10px] ${REL_STYLE[(f.relevance || '').toLowerCase()] || REL_STYLE.niedrig}`}>
                            {f.relevance} · {f.affected_area}
                          </span>
                        </div>
                        <p className="mt-1 text-xs text-gray-600">{f.summary}</p>
                        {f.action_needed && <p className="mt-1 text-xs font-medium text-amber-700">→ {f.action_needed}</p>}
                        <a href={f.source} target="_blank" rel="noreferrer" className="text-xs text-emerald-700 hover:underline">Quelle</a>
                      </div>
                    ))}
                    {s.status === 'ok' && (s.findings || []).length === 0 && (
                      <p className="text-xs text-gray-500">Keine relevanten Meldungen in diesem Lauf.</p>
                    )}
                    <p className="text-[11px] text-gray-400">
                      Quellen: {(s.sources || []).map((q) => `${q.name} (${q.ok ? 'OK' : 'Fehler'})`).join(' · ')}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
